import { useCallback, useRef } from 'react';

import { updateRfqFileCaption } from '../api/updateRfqFileCaption';
import {
  PendingFileWithCaption,
  useOptimisticFileUploadWithCaption
} from './useOptimisticFileUploadWithCaption';

/**
 * Hook for managing file captions - handles both uploaded and pending files
 */
export function useFileCaptionManager(
  rfqId: number | undefined,
  uploadHook: ReturnType<typeof useOptimisticFileUploadWithCaption>
) {
  const { updateCaption, uploadFileWithCaption } = uploadHook;
  const pendingFilesRef = useRef(uploadHook.pendingFiles);

  // Keep ref in sync
  pendingFilesRef.current = uploadHook.pendingFiles;

  // Helper to get pending file by ID or by matching the file
  const getPendingFileByIdOrFile = useCallback(
    (pendingFileId: string | undefined, file: File | undefined) => {
      if (pendingFileId) {
        const found = pendingFilesRef.current.find(
          (pf) => pf.id === pendingFileId
        );

        if (found) return found;
      }
      if (file) {
        return pendingFilesRef.current.find(
          (pf) =>
            pf.file === file ||
            (pf.file.name === file.name &&
              pf.file.size === file.size &&
              pf.file.lastModified === file.lastModified)
        );
      }

      return undefined;
    },
    []
  );

  // Helper to update caption - handles both uploaded and pending files
  const updateCaptionForFile = useCallback(
    async (
      pendingFile: PendingFileWithCaption | undefined,
      caption: string
    ) => {
      if (!pendingFile) return;

      // If file is already uploaded, update caption via PATCH endpoint
      if (pendingFile.uploadedFile) {
        if (!rfqId) {
          throw new Error('RFQ ID is required');
        }

        try {
          await updateRfqFileCaption({
            rfq_id: rfqId,
            file_id: pendingFile.uploadedFile.id,
            caption: caption || ''
          });

          // Update local state to reflect the new caption
          updateCaption(pendingFile.id, caption);
        } catch (error) {
          console.error('Error updating file caption:', error);
          // Still update local state even if backend update fails
          updateCaption(pendingFile.id, caption);
          throw error;
        }
      } else {
        // File not uploaded yet, just update local state
        updateCaption(pendingFile.id, caption);

        // Start upload if caption was just added and file hasn't been uploaded yet
        if (caption.trim() && pendingFile.status === 'pending') {
          uploadFileWithCaption(pendingFile.id, caption);
        }
      }
    },
    [rfqId, updateCaption, uploadFileWithCaption]
  );

  return {
    getPendingFileByIdOrFile,
    updateCaptionForFile
  };
}
